import { useEffect, useMemo, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import SubmissionControlCard from './SubmissionControlCard';
import SyncSubmittedDataSection from './SyncSubmittedDataSection';
import { SubmissionCardModel, SubmissionRow } from './types';
import { useAppContext } from '../../Context';
import httpService from '../../services/httpService';
import { ApiEndpoints, Constants } from '../../constants/appConstants';
import { alphaNumericSort } from '../ProviderProfile/models';

const REVIEW_COMPLETED_STATUSES = ['Review Completed', 'Resolution Review Completed'];

interface ProviderTypeSelectOption {
  label: string;
  value: string;
}

const defaultProviderTypes: ProviderTypeSelectOption[] = [
  { label: 'Support Coordination Organizations', value: 'SCO' },
  { label: 'Residential Providers', value: 'RS' }
];

const createDefaultCard = (
  title: string,
  distributeLabel: string,
  measureSyncLabel: string
): SubmissionCardModel => ({
  title,
  calculateDisabled: false,
  publishDisabled: false,
  initialReviewCycleEnabled: false,
  resolutionReviewCycleEnabled: true,
  selectedSubmissions: [],
  distributeLabel,
  measureSyncLabel
});

const SubmissionLevelControlsSection = (props: any) => {
  const {
    providerTypes,
    providerType: contextProviderType,
    reviewYear: contextReviewYear
  } = useAppContext();

  const providerType = props.providerType ?? contextProviderType;
  const reviewYear = props.reviewYear ?? contextReviewYear;
  const toastRef = useRef<Toast>(null);

  const [isMeasureLibraryLocked, setIsMeasureLibraryLocked] = useState(false);
  const [isDucLocked, setIsDucLocked] = useState(false);

  const [pbcCard, setPbcCard] = useState<SubmissionCardModel>(
    createDefaultCard(
      'Performance-Based Contracting (PBC)',
      'Distribute PBC Letters',
      'Sync Measure Details'
    )
  );

  const [p4pCard, setP4pCard] = useState<SubmissionCardModel>(
    createDefaultCard(
      'Pay for Performance (P4P)',
      'Distribute P4P Letters',
      'Sync Measure Details'
    )
  );

  const [selectedPbcMeasures, setSelectedPbcMeasures] = useState<any[]>([]);
  const [availableMeasures, setAvailableMeasures] = useState<any[]>([]);
  const [availableSubmissions, setAvailableSubmissions] = useState<SubmissionRow[]>([]);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);
  const [pbcPublishLoading, setPbcPublishLoading] = useState(false);
  const [pbcDistributeLoading, setPbcDistributeLoading] = useState(false);
  const [pbcMeasureSyncLoading, setPbcMeasureSyncLoading] = useState(false);
  const [p4pMeasureSyncLoading, setP4pMeasureSyncLoading] = useState(false);
  const [p4pDistributeLoading, setP4pDistributeLoading] = useState(false);
  const [showEmailSentDialog, setShowEmailSentDialog] = useState(false);

  const [pendingDistribute, setPendingDistribute] = useState<{
    appType: 'PBC' | 'PFP';
    card: SubmissionCardModel;
    setLoading: (v: boolean) => void;
  } | null>(null);

  const [pbcInitialToggleLoading, setPbcInitialToggleLoading] = useState(false);
  const [pbcResolutionToggleLoading, setPbcResolutionToggleLoading] = useState(false);
  const [p4pInitialToggleLoading, setP4pInitialToggleLoading] = useState(false);
  const [p4pResolutionToggleLoading, setP4pResolutionToggleLoading] = useState(false);
  const [stdMeasureToggleLoading, setStdMeasureToggleLoading] = useState(false);
  const [dataUploadToggleLoading, setDataUploadToggleLoading] = useState(false);

  const uniqueMeasureOptions = useMemo(() => {
    const seen = new Set<string>();

    return availableMeasures.filter((m) => {
      if (seen.has(m.measureNumber)) {
        return false;
      }

      seen.add(m.measureNumber);
      return true;
    });
  }, [availableMeasures]);

  const providerTypeOptions = useMemo<ProviderTypeSelectOption[]>(() => {
    if (providerTypes?.length) {
      return providerTypes.map((p) => ({
        label: p.providerType,
        value: p.providerCode
      }));
    }

    return defaultProviderTypes;
  }, [providerTypes]);

  const selectedProviderType =
    providerType ||
    providerTypeOptions[0]?.value ||
    defaultProviderTypes[0].value;

  useEffect(() => {
    if (!providerType && providerTypes?.length) {
      props.setLocalProviderType(
        providerTypes[0].providerCode || defaultProviderTypes[0].value
      );
    }
  }, [providerType, providerTypes]);

  useEffect(() => {
    if (!selectedProviderType || !reviewYear) {
      return;
    }

    fetchMeasures();
    fetchProviders();
    fetchReviewCycleStates();
    getLibraryLockFeature();
  }, [selectedProviderType, reviewYear]);

  const fetchReviewCycleStates = async () => {
    try {
      const pbcReviewResponse = await httpService.get(
        `${ApiEndpoints.IsActiveReview}/${selectedProviderType}`
      );

      setPbcCard((prev) => ({
        ...prev,
        initialReviewCycleEnabled: pbcReviewResponse?.data === true
      }));

      const pbcDisputeResponse = await httpService.get(
        `${ApiEndpoints.IsActiveDisputeCycle}/${selectedProviderType}`
      );

      setPbcCard((prev) => ({
        ...prev,
        resolutionReviewCycleEnabled: pbcDisputeResponse?.data === true
      }));

      const pfpReviewResponse = await httpService.get(
        ApiEndpoints.IsPfpActiveReview
      );

      setP4pCard((prev) => ({
        ...prev,
        initialReviewCycleEnabled: pfpReviewResponse?.data === true
      }));

      const pfpDisputeResponse = await httpService.get(
        ApiEndpoints.IsPfpActiveDisputeCycle
      );

      setP4pCard((prev) => ({
        ...prev,
        resolutionReviewCycleEnabled: pfpDisputeResponse?.data === true
      }));
    } catch (error) {
      console.error('SubmissionLevelControlsSection.fetchReviewCycleStates failed.', error);
    }
  };

  const fetchMeasures = async () => {
    try {
      const response = await httpService.get(
        `${ApiEndpoints.getCalculatedMeasures}/${selectedProviderType}/${reviewYear}`
      );

      const sorted = [...(response.data as any[])].sort((a, b) =>
        alphaNumericSort(a.measureNumber, b.measureNumber)
      );

      setAvailableMeasures(sorted);
      setSelectedPbcMeasures([]);
    } catch (error) {
      console.error('SubmissionLevelControlsSection.fetchMeasures failed.', error);
      setAvailableMeasures([]);
    }
  };

  const fetchProviders = async () => {
    setSubmissionsLoading(true);

    try {
      const endpoint =
        `${ApiEndpoints.getProvidersAdminData}/${selectedProviderType}/${reviewYear}`;

      const response = await httpService.get(endpoint);

      const providers: SubmissionRow[] = (response.data as any[])
        .filter((p) => REVIEW_COMPLETED_STATUSES.includes(p.reviewStatus))
        .map((p) => ({
          id: String(p.providerId),
          label: `${p.providerAgencyName} - MPI ${p.mpi}`,
          mpi: String(p.mpi),
          isEmailSent: p.isEmailSent
        }));

      setAvailableSubmissions(providers);

      const allIds = providers.map((p) => p.id);

      setPbcCard((prev) => ({
        ...prev,
        selectedSubmissions: allIds
      }));

      setP4pCard((prev) => ({
        ...prev,
        selectedSubmissions: allIds
      }));
    } catch (error) {
      console.error('SubmissionLevelControlsSection.fetchProviders failed.', error);
      setAvailableSubmissions([]);
    } finally {
      setSubmissionsLoading(false);
    }
  };

  const handlePbcInitialToggle = async () => {
    setPbcInitialToggleLoading(true);

    try {
      if (pbcCard.initialReviewCycleEnabled) {
        const response = await httpService.get(
          `${ApiEndpoints.EndReviewCycle}/${selectedProviderType}`
        );

        if (response.status === 200) {
          setPbcCard((prev) => ({
            ...prev,
            initialReviewCycleEnabled: false
          }));

          showToast(
            'success',
            'Review Cycle',
            'PBC Initial Review Cycle ended successfully.'
          );
        }
      } else {
        const response = await httpService.get(
          `${ApiEndpoints.BeginReviewCycle}/${selectedProviderType}`
        );

        if (response.status === 200) {
          setPbcCard((prev) => ({
            ...prev,
            initialReviewCycleEnabled: true
          }));

          showToast(
            'success',
            'Review Cycle',
            'PBC Initial Review Cycle started successfully.'
          );

          fetchProviders();
        }
      }
    } catch (error) {
      console.error('SubmissionLevelControlsSection.handlePbcInitialToggle failed.', error);
      showToast('error', 'Error', 'Failed to toggle PBC Initial Review Cycle.');
    } finally {
      setPbcInitialToggleLoading(false);
    }
  };

  const handlePbcResolutionToggle = async () => {
    setPbcResolutionToggleLoading(true);

    try {
      if (pbcCard.resolutionReviewCycleEnabled) {
        const response = await httpService.get(
          `${ApiEndpoints.EndDisputeCycle}/${selectedProviderType}`
        );

        if (response.status === 200) {
          setPbcCard((prev) => ({
            ...prev,
            resolutionReviewCycleEnabled: false
          }));

          showToast(
            'success',
            'Dispute Cycle',
            'PBC Resolution Review Cycle ended successfully.'
          );

          fetchProviders();
        }
      } else {
        const response = await httpService.get(
          `${ApiEndpoints.BeginDisputeCycle}/${selectedProviderType}`
        );

        if (response.status === 200) {
          setPbcCard((prev) => ({
            ...prev,
            resolutionReviewCycleEnabled: true
          }));

          await httpService.get(
            `${ApiEndpoints.ChangeReviewStatusForDispute}/${selectedProviderType}`
          );

          showToast(
            'success',
            'Dispute Cycle',
            'PBC Resolution Review Cycle started successfully.'
          );

          fetchProviders();
        }
      }
    } catch (error) {
      console.error('SubmissionLevelControlsSection.handlePbcResolutionToggle failed.', error);
      showToast('error', 'Error', 'Failed to toggle PBC Resolution Review Cycle.');
    } finally {
      setPbcResolutionToggleLoading(false);
    }
  };

  const handleP4pInitialToggle = async () => {
    setP4pInitialToggleLoading(true);

    try {
      if (p4pCard.initialReviewCycleEnabled) {
        const response = await httpService.get(
          ApiEndpoints.EndPfpReviewCycle
        );

        if (response.status === 200) {
          setP4pCard((prev) => ({
            ...prev,
            initialReviewCycleEnabled: false
          }));

          showToast(
            'success',
            'Review Cycle',
            'P4P Initial Review Cycle ended successfully.'
          );
        }
      } else {
        const response = await httpService.get(
          ApiEndpoints.BeginPfpReviewCycle
        );

        if (response.status === 200) {
          setP4pCard((prev) => ({
            ...prev,
            initialReviewCycleEnabled: true
          }));

          showToast(
            'success',
            'Review Cycle',
            'P4P Initial Review Cycle started successfully.'
          );

          fetchProviders();
        }
      }
    } catch (error) {
      console.error('SubmissionLevelControlsSection.handleP4pInitialToggle failed.', error);
      showToast('error', 'Error', 'Failed to toggle P4P Initial Review Cycle.');
    } finally {
      setP4pInitialToggleLoading(false);
    }
  };

  const handleP4pResolutionToggle = async () => {
    setP4pResolutionToggleLoading(true);

    try {
      if (p4pCard.resolutionReviewCycleEnabled) {
        const response = await httpService.get(
          ApiEndpoints.EndPfpDisputeCycle
        );

        if (response.status === 200) {
          setP4pCard((prev) => ({
            ...prev,
            resolutionReviewCycleEnabled: false
          }));

          showToast(
            'success',
            'Dispute Cycle',
            'P4P Resolution Review Cycle ended successfully.'
          );

          fetchProviders();
        }
      } else {
        const response = await httpService.get(
          ApiEndpoints.BeginPfpDisputeCycle
        );

        if (response.status === 200) {
          setP4pCard((prev) => ({
            ...prev,
            resolutionReviewCycleEnabled: true
          }));

          await httpService.get(
            `${ApiEndpoints.ChangePfpReviewStatusForDispute}/${selectedProviderType}`
          );

          showToast(
            'success',
            'Dispute Cycle',
            'P4P Resolution Review Cycle started successfully.'
          );

          fetchProviders();
        }
      }
    } catch (error) {
      console.error('SubmissionLevelControlsSection.handleP4pResolutionToggle failed.', error);
      showToast('error', 'Error', 'Failed to toggle P4P Resolution Review Cycle.');
    } finally {
      setP4pResolutionToggleLoading(false);
    }
  };

  const getLibraryLockFeature = () => {
    httpService
      .get(
        `${ApiEndpoints.GetProviderFeature}/${selectedProviderType}/${Constants.MEASURE_LIBRARY_LOCKED}`
      )
      .then((response: any) => {
        if (response.status === 200) {
          setIsMeasureLibraryLocked(response?.data.isEnabled);
        }
      })
      .catch((error: any) => {
        console.error(
          'SubmissionLevelControlsSection.getLibraryLockFeature measure library failed.',
          error
        );
      });

    httpService
      .get(
        `${ApiEndpoints.GetProviderFeature}/${selectedProviderType}/${Constants.DATA_UPLOAD_CENTER_LOCKED}`
      )
      .then((response: any) => {
        if (response.status === 200) {
          setIsDucLocked(response?.data.isEnabled);
        }
      })
      .catch((error: any) => {
        console.error(
          'SubmissionLevelControlsSection.getLibraryLockFeature data upload center failed.',
          error
        );
      });
  };

  const addUpdateFeature = (
    featureCode: string,
    isEnabled: boolean,
    setLoading: (v: boolean) => void,
    setLockState: (v: boolean) => void
  ) => {
    setLoading(true);

    httpService
      .post(`${ApiEndpoints.AddUpdateProviderFeature}`, {
        providerType: selectedProviderType,
        featureCode: featureCode,
        isEnabled: !isEnabled
      })
      .then((response: any) => {
        if (response.status === 200) {
          showToast(
            'success',
            'Locking',
            `${isEnabled ? 'Locked' : 'Unlocked'} successfully`
          );

          setLockState(!isEnabled);
        } else {
          showToast(
            'error',
            'Locking',
            `Unable to ${isEnabled ? 'Lock' : 'Unlock'}`
          );
        }
      })
      .catch((error: any) => {
        console.error('SubmissionLevelControlsSection.addUpdateFeature failed.', error);

        showToast(
          'error',
          'Locking',
          `Unable to ${isEnabled ? 'Lock' : 'Unlock'}`
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const showToast = (
    severity: 'success' | 'info' | 'warn' | 'error',
    summary: string,
    detail: string
  ) => {
    toastRef.current?.show({
      severity,
      summary,
      detail,
      life: 3000
    });
  };

  const handleProviderTypeChange = (value: string) => {
    props.setLocalProviderType(value);

    setPbcCard((prev) => ({
      ...prev,
      selectedSubmissions: []
    }));

    setP4pCard((prev) => ({
      ...prev,
      selectedSubmissions: []
    }));
  };

  const handleCalculatePbc = async () => {
    if (selectedPbcMeasures.length === 0) {
      showToast(
        'warn',
        'Validation',
        'Please select at least one measure to perform calculations.'
      );
      return;
    }

    try {
      showToast(
        'info',
        'Calculations',
        'Calculations in progress. Please wait...'
      );

      const standardMeasureIds = [
        ...new Set(
          availableMeasures
            .filter((m) => selectedPbcMeasures.includes(m.measureNumber))
            .map((m) => m.standardMeasureId)
        )
      ];

      if (standardMeasureIds.length === 0) {
        showToast(
          'error',
          'Calculation Error',
          'Unable to resolve Standard Measure IDs for the selected measures.'
        );
        return;
      }

      await httpService.post(ApiEndpoints.performCalculations, {
        standardMeasureIds,
        providerType: selectedProviderType
      });

      showToast(
        'success',
        'Success',
        'Calculations performed successfully.'
      );
    } catch (error) {
      console.error('SubmissionLevelControlsSection.handleCalculatePbc failed.', error);

      showToast(
        'error',
        'Error',
        'Error performing calculations. Please try again later.'
      );
    }
  };

  const publishPbcToDataSubmissionPortal = async () => {
    setPbcPublishLoading(true);

    try {
      await httpService.get(
        `${ApiEndpoints.pushCalculatedData}/${selectedProviderType}`
      );

      showToast(
        'success',
        'Publish Status',
        'Calculated data pushed to Intake successfully.'
      );
    } catch (error) {
      console.error(
        'SubmissionLevelControlsSection.publishPbcToDataSubmissionPortal failed.',
        error
      );

      showToast(
        'error',
        'Publish Error',
        'Error pushing calculated data to PBC. Please try again later.'
      );
    } finally {
      setPbcPublishLoading(false);
    }
  };

  const showUnderDevelopment = () =>
    showToast('info', 'P4P', 'Under Development');

  const executeDistributeLetters = async (
    appType: 'PBC' | 'PFP',
    card: SubmissionCardModel,
    setLoading: (v: boolean) => void
  ) => {
    const mpiIds = availableSubmissions
      .filter((s) => card.selectedSubmissions.includes(s.id))
      .map((s) => s.mpi);

    const payload = {
      AppType: appType,
      ReviewYear: reviewYear,
      ProviderType: selectedProviderType,
      IsDispute: card.resolutionReviewCycleEnabled,
      MpiIds: mpiIds
    };

    setLoading(true);

    try {
      await httpService.post(
        ApiEndpoints.SendEmailToMpis,
        payload
      );

      showToast(
        'success',
        'Distribution',
        `${appType.toUpperCase()} letters distributed successfully.`
      );
    } catch (error) {
      console.error(
        `SubmissionLevelControlsSection.executeDistributeLetters failed for ${appType}.`,
        error
      );

      showToast(
        'error',
        'Distribution Error',
        `Error distributing ${appType.toUpperCase()} letters. Please try again later.`
      );
    } finally {
      setLoading(false);
      fetchProviders();
    }
  };

  const handleDistributeLetters = async (
    appType: 'PBC' | 'PFP',
    card: typeof pbcCard,
    setLoading: (v: boolean) => void
  ) => {
    if (!reviewYear) {
      showToast(
        'warn',
        'Validation',
        'Review year is not set.'
      );
      return;
    }

    if (card.selectedSubmissions.length === 0) {
      showToast(
        'warn',
        'Validation',
        'Please select at least one submission for letter distribution.'
      );
      return;
    }

    const hasEmailAlreadySent = availableSubmissions.some(
      (s) =>
        card.selectedSubmissions.includes(s.id) &&
        s.isEmailSent === true
    );

    if (hasEmailAlreadySent) {
      setPendingDistribute({
        appType,
        card,
        setLoading
      });

      setShowEmailSentDialog(true);
      return;
    }

    await executeDistributeLetters(
      appType,
      card,
      setLoading
    );
  };

  const executeMeasureSync = async (
    appType: 'PBC' | 'PFP',
    card: SubmissionCardModel,
    setLoading: (v: boolean) => void
  ) => {
    const mpiIds = availableSubmissions
      .filter((s) => card.selectedSubmissions.includes(s.id))
      .map((s) => s.mpi);

    const payload = {
      ReviewYear: reviewYear,
      ProviderType: selectedProviderType,
      IsDispute: card.resolutionReviewCycleEnabled,
      Mpis: mpiIds
    };

    setLoading(true);

    try {
      await httpService.post(
        ApiEndpoints.SyncMeasureDetailsToIntake,
        payload
      );

      showToast(
        'success',
        'Measure Sync',
        `${appType.toUpperCase()} measure details synced successfully.`
      );
    } catch (error) {
      console.error(
        `SubmissionLevelControlsSection.executeMeasureSync failed for ${appType}.`,
        error
      );

      showToast(
        'error',
        'Sync Error',
        `Error synching ${appType.toUpperCase()} measure details. Please try again later.`
      );
    } finally {
      setLoading(false);
      fetchProviders();
    }
  };

  const handleMeasureSync = async (
    appType: 'PBC' | 'PFP',
    card: typeof pbcCard,
    setLoading: (v: boolean) => void
  ) => {
    if (!reviewYear) {
      showToast(
        'warn',
        'Validation',
        'Review year is not set.'
      );
      return;
    }

    if (card.selectedSubmissions.length === 0) {
      showToast(
        'warn',
        'Validation',
        'Please select at least one submission for letter distribution.'
      );
      return;
    }

    await executeMeasureSync(
      appType,
      card,
      setLoading
    );
  };

  const onConfirmDistribute = async () => {
    setShowEmailSentDialog(false);

    if (pendingDistribute) {
      const {
        appType,
        card,
        setLoading
      } = pendingDistribute;

      setPendingDistribute(null);

      await executeDistributeLetters(
        appType,
        card,
        setLoading
      );
    }
  };

  const onCancelDistribute = () => {
    setShowEmailSentDialog(false);
    setPendingDistribute(null);
  };

  return (
    <>
      <Toast ref={toastRef} position="top-right" />

      <Dialog
        header="Confirmation"
        visible={showEmailSentDialog}
        onHide={onCancelDistribute}
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              label="No"
              icon="pi pi-times"
              onClick={onCancelDistribute}
              className="p-button-text"
            />

            <Button
              label="Yes"
              icon="pi pi-check"
              onClick={onConfirmDistribute}
              autoFocus
            />
          </div>
        }
      >
        <p>
          Some providers have already received emails. Do you want to proceed and resend?
        </p>
      </Dialog>

      <div className='admin-field provider-type-field submission-provider-select'>
        <label htmlFor='providerType'>Provider Type</label>

        <select
          id='providerType'
          value={selectedProviderType}
          onChange={(e) =>
            handleProviderTypeChange(e.target.value)
          }
        >
          {providerTypeOptions.map((option) => (
            <option
              key={option.value}
              value={option.value}
            >
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className='submission-subsection'>
        <h4 className='submission-subsection-title'>
          Sync Submitted Data
        </h4>

        <SyncSubmittedDataSection
          onShowToast={showToast}
        />
      </div>

      <div className='submission-subsection'>
        <h4 className='submission-subsection-title'>
          Controls
        </h4>

        <div className='submission-lock-row'>
          <strong
            style={{
              minWidth: '220px',
              display: 'inline-block'
            }}
          >
            Measure and Standard Library
          </strong>

          {stdMeasureToggleLoading ? (
            <i
              className='pi pi-spin pi-spinner'
              style={{
                fontSize: '1.2rem',
                color: '#6c757d',
                margin: '0 0.5rem'
              }}
            />
          ) : (
            <>
              <span>Unlocked</span>

              <button
                type='button'
                className={`admin-switch ${isMeasureLibraryLocked ? 'off' : 'on'}`}
                onClick={() =>
                  addUpdateFeature(
                    Constants.MEASURE_LIBRARY_LOCKED,
                    isMeasureLibraryLocked,
                    setStdMeasureToggleLoading,
                    setIsMeasureLibraryLocked
                  )
                }
                aria-label='Toggle measure and standard library lock status'
              >
                <span />
              </button>

              <span>Locked</span>
            </>
          )}
        </div>

        <div className='submission-lock-row'>
          <strong
            style={{
              minWidth: '220px',
              display: 'inline-block'
            }}
          >
            Data Upload Center
          </strong>

          {dataUploadToggleLoading ? (
            <i
              className='pi pi-spin pi-spinner'
              style={{
                fontSize: '1.2rem',
                color: '#6c757d',
                margin: '0 0.5rem'
              }}
            />
          ) : (
            <>
              <span>Unlocked</span>

              <button
                type='button'
                className={`admin-switch ${isDucLocked ? 'off' : 'on'}`}
                onClick={() =>
                  addUpdateFeature(
                    Constants.DATA_UPLOAD_CENTER_LOCKED,
                    isDucLocked,
                    setDataUploadToggleLoading,
                    setIsDucLocked
                  )
                }
                aria-label='Toggle data upload center lock status'
              >
                <span />
              </button>

              <span>Locked</span>
            </>
          )}
        </div>

        <div className='submission-cards-grid'>
          <SubmissionControlCard
            data={pbcCard}
            measureOptions={uniqueMeasureOptions}
            submissionOptions={availableSubmissions}
            selectedMeasures={selectedPbcMeasures}
            onMeasureChange={(measures) => {
              setSelectedPbcMeasures(measures);

              setPbcCard((prev) => ({
                ...prev,
                publishDisabled: false
              }));
            }}
            onCalculateClick={handleCalculatePbc}
            onInitialToggle={handlePbcInitialToggle}
            onResolutionToggle={handlePbcResolutionToggle}
            onSubmissionChange={(selectedSubmissions) =>
              setPbcCard((prev) => ({
                ...prev,
                selectedSubmissions
              }))
            }
            onPublishClick={publishPbcToDataSubmissionPortal}
            onDistributeClick={() =>
              handleDistributeLetters(
                'PBC',
                pbcCard,
                setPbcDistributeLoading
              )
            }
            onMeasureSyncClick={() =>
              handleMeasureSync(
                'PBC',
                pbcCard,
                setPbcMeasureSyncLoading
              )
            }
            publishLoading={pbcPublishLoading}
            distributeLoading={pbcDistributeLoading}
            measureSyncLoading={pbcMeasureSyncLoading}
            submissionsLoading={submissionsLoading}
            initialToggleLoading={pbcInitialToggleLoading}
            resolutionToggleLoading={pbcResolutionToggleLoading}
            initialToggleDisabled={pbcCard.resolutionReviewCycleEnabled}
          />

          <SubmissionControlCard
            data={p4pCard}
            measureOptions={[]}
            submissionOptions={availableSubmissions}
            onMeasureChange={() =>
              setP4pCard((prev) => ({
                ...prev,
                publishDisabled: false
              }))
            }
            onCalculateClick={showUnderDevelopment}
            onInitialToggle={handleP4pInitialToggle}
            onResolutionToggle={handleP4pResolutionToggle}
            onSubmissionChange={(selectedSubmissions) =>
              setP4pCard((prev) => ({
                ...prev,
                selectedSubmissions
              }))
            }
            onPublishClick={showUnderDevelopment}
            onDistributeClick={() =>
              handleDistributeLetters(
                'PFP',
                p4pCard,
                setP4pDistributeLoading
              )
            }
            onMeasureSyncClick={() =>
              handleMeasureSync(
                'PFP',
                p4pCard,
                setP4pMeasureSyncLoading
              )
            }
            measureSyncLoading={p4pMeasureSyncLoading}
            distributeLoading={p4pDistributeLoading}
            submissionsLoading={submissionsLoading}
            initialToggleLoading={p4pInitialToggleLoading}
            resolutionToggleLoading={p4pResolutionToggleLoading}
            initialToggleDisabled={p4pCard.resolutionReviewCycleEnabled}
          />
        </div>
      </div>
    </>
  );
};

export default SubmissionLevelControlsSection;